import { Icon } from '@iconify/react'
import { batch, Observable } from '@legendapp/state'
import { Reactive, reactive, Show, useObservable } from '@legendapp/state/react'
import { useLocation } from '@tanstack/react-router'
import { motion, useAnimationControls, Variants } from 'framer-motion'
import { isEmpty } from 'radash'
import { FC, forwardRef, isValidElement, PropsWithChildren, ReactNode, useEffect } from 'react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~in-app/components'
import { nativeMenuItemIconAnimationVariants } from '~in-app/components/layout/console/native-sidebar/animation'
import { NativeMenuRecursiveRender } from '~in-app/components/layout/console/native-sidebar/NativeMenuRecursiveRender'
import { cn, formatUrlPathname } from '~in-app/lib/utils'
import { MenuItemProps } from '~in-app/routerList'

interface NativeCollapseMenuItemProps {
  name: string
  label: ReactNode | string
  icon?: ReactNode | string
  path: string
  items?: MenuItemProps[]
}

interface WrapperProps {
  path: string
  parentState$: Observable<State$>
}

interface State$ {
  isMenuOpen: boolean
  hasActiveChildren: boolean
  changeHasActiveChildren: (active: boolean) => void
  onMenuOpenChange: (open: boolean) => void
}

const openAnimationVariants: Variants = {
  init: {
    rotate: 0,
  },
  true: {
    rotate: 90,
  },
  false: {
    rotate: 0,
  },
  exit: {
  },
}

const ReactiveCollapsible = reactive(Collapsible)

const Wrapper = forwardRef<HTMLDivElement, PropsWithChildren<WrapperProps>>(({ parentState$, path, children }, ref) => {
  const location = useLocation()
  const state$ = useObservable({ isActive: false })

  useEffect(() => {
    const cleanedPath = path || ''
    const currentPath = location.pathname || ''
    const hasActiveChildren = !isEmpty(cleanedPath) && currentPath.startsWith(cleanedPath)
    parentState$.changeHasActiveChildren(hasActiveChildren)

    state$.isActive.set(hasActiveChildren)
  }, [path, location])

  return (
    <Reactive.div
      ref={ref}
      $className={
        () => cn('flex items-center rounded-md px-4 py-2 text-muted-foreground hover:bg-muted', {
          'bg-muted text-foreground': parentState$.isMenuOpen.get(),
          'bg-primary/5 dark:text-primary-400 text-primary-600 dark:bg-primary/20 dark:hover:bg-primary/30 hover:bg-primary/10': state$.isActive.get(),
        })
      }
    >
      {children}
    </Reactive.div>
  )
})

const NativeCollapseMenuItem: FC<NativeCollapseMenuItemProps> = ({ icon, label, path, items, name }) => {
  const collapseControl = useAnimationControls()
  const state$ = useObservable<State$>({
    isMenuOpen: false,
    hasActiveChildren: false,
    changeHasActiveChildren: (active: boolean) => {
      batch(() => {
        state$.hasActiveChildren.set(active)
      })
    },
    onMenuOpenChange: (open: boolean) => {
      batch(() => {
        state$.isMenuOpen.set(open)
      })
    },
  })

  useEffect(() => {
    // only for the first time render
    if (state$.hasActiveChildren.get()) {
      collapseControl.start('true')
      state$.onMenuOpenChange(true)
    }
  }, [])

  useEffect(() => {
    const isMenuOpenDispose = state$.isMenuOpen.onChange(({ value }) => {
      collapseControl.start(String(value))
    })

    return () => {
      isMenuOpenDispose()
    }
  }, [])

  return (
    <ReactiveCollapsible
      $open={state$.isMenuOpen}
      onOpenChange={state$.onMenuOpenChange}
    >
      <CollapsibleTrigger asChild>
        <li className="w-full cursor-pointer outline-none">
          <Wrapper
            parentState$={state$}
            path={path}
          >
            <motion.span variants={nativeMenuItemIconAnimationVariants}>
              <Show
                if={isValidElement(icon)}
                else={
                  (
                    <Show
                      if={typeof icon === 'string'}
                      else={
                        (
                          <Icon
                            height="18"
                            icon="lucide:dot"
                            width="18"
                          />
                        )
                      }
                    >
                      {
                        () => (
                          <Icon
                            height="18"
                            icon={icon as string}
                            width="18"
                          />
                        )
                      }
                    </Show>
                  )
                }
              >
                {() => icon}
              </Show>
            </motion.span>

            <motion.div className="flex w-full items-center justify-between overflow-hidden">
              <p className={
                cn('min-w-0 truncate text-sm font-normal select-none capitalize')
              }
              >
                <Show
                  else={(label as string)}
                  if={isValidElement(label)}
                >
                  {() => label}
                </Show>
              </p>

              <span>
                <svg
                  height="18"
                  viewBox="0 0 24 24"
                  width="18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <motion.path
                    animate={collapseControl}
                    d="m9 18l6-6l-6-6"
                    fill="none"
                    initial="init"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    variants={openAnimationVariants}
                    transition={
                      {
                        ease: 'easeInOut',
                      }
                    }
                  />
                </svg>

              </span>
            </motion.div>
          </Wrapper>
        </li>
      </CollapsibleTrigger>

      <CollapsibleContent className={cn('overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down')}>
        <ul className="ml-2 mt-2 flex flex-col gap-2">
          {
            (items || []).map(props => (
              <NativeMenuRecursiveRender
                key={`NativeCollapseMenuItem_${props.label}_${props.path}`}
                hasParent={true}
                icon={props.icon}
                items={props.children}
                label={props.label}
                path={formatUrlPathname(path + (props.path || ''))}
              />
            ))
          }
        </ul>
      </CollapsibleContent>
    </ReactiveCollapsible>
  )
}
NativeCollapseMenuItem.displayName = 'NativeCollapseMenuItem'

export { NativeCollapseMenuItem }
